import { CheckCircle2, AlertTriangle, XCircle, Sparkles, Globe, FileDown, Share2, Copy, Check, Lock } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SeoFixes from "@/components/SeoFixes";
import Backlinks from "@/components/Backlinks";
import CoreWebVitals from "@/components/CoreWebVitals";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  Tooltip,
} from "recharts";

const STATUS = {
  good: { color: "#00C853", Icon: CheckCircle2 },
  warning: { color: "#FFD600", Icon: AlertTriangle },
  error: { color: "#FF3B30", Icon: XCircle },
};

const PRIORITY = {
  high: "bg-bad/10 text-bad border-bad/20",
  medium: "bg-warn/20 text-yellow-700 border-yellow-300",
  low: "bg-good/10 text-green-700 border-good/20",
};

function ScoreGauge({ score }) {
  const radius = 80;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;
  const color = score >= 80 ? "#00C853" : score >= 50 ? "#FFD600" : "#FF3B30";
  const rating = score >= 80 ? "Excellent" : score >= 50 ? "Needs Work" : "Poor";
  return (
    <div className="flex flex-col items-center justify-center">
      <div className="relative w-[200px] h-[200px]">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 200 200">
          <circle cx="100" cy="100" r={radius} fill="none" stroke="#F3F4F6" strokeWidth="14" />
          <circle
            cx="100"
            cy="100"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 1s ease-out" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-heading text-6xl font-black tracking-tighter" data-testid="seo-score-value">
            {score}
          </span>
          <span className="uppercase-label mt-1">/ 100</span>
        </div>
      </div>
      <span
        className="mt-4 px-4 py-1 rounded-full text-sm font-bold"
        style={{ backgroundColor: `${color}1A`, color }}
      >
        {rating}
      </span>
    </div>
  );
}

function CheckList({ title, checks, testid }) {
  return (
    <div
      className="bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden"
      data-testid={testid}
    >
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="uppercase-label">{title}</h3>
      </div>
      <div className="divide-y divide-gray-100 max-h-[420px] overflow-y-auto">
        {checks.map((c) => {
          const s = STATUS[c.status] || STATUS.warning;
          return (
            <div key={c.id} className="flex gap-3 px-5 py-3.5" data-testid={`check-${c.id}`}>
              <s.Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: s.color }} strokeWidth={1.75} />
              <div className="min-w-0">
                <div className="font-semibold text-sm text-gray-900">{c.label}</div>
                <div className="text-sm text-gray-500">{c.message}</div>
                {c.detail && (
                  <div className="text-xs text-gray-400 mt-1 truncate italic">{c.detail}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AnalysisResult({ report }) {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(!!report.unlocked);
  const [unlocking, setUnlocking] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const kwData = (report.keywords || []).slice(0, 8).map((k) => ({
    name: k.keyword,
    density: k.density,
    count: k.count,
  }));

  const doUnlock = async () => {
    setUnlocking(true);
    try {
      await api.post(`/reports/${report.id}/unlock`);
      setUnlocked(true);
      await refreshUser();
      toast.success("Report unlocked", { description: "PDF, AI fixes & WordPress push are now available." });
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("You're out of credits", { description: "Taking you to billing…" });
        setTimeout(() => navigate("/billing"), 900);
      } else {
        toast.error("Could not unlock", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
      }
    } finally {
      setUnlocking(false);
    }
  };

  const downloadPdf = async () => {
    if (!report.id) return;
    setDownloading(true);
    try {
      const res = await api.get(`/reports/${report.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `seo_report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  const openShare = async () => {
    if (!report.id) return;
    setSharing(true);
    try {
      const { data } = await api.post(`/reports/${report.id}/share`);
      setShareUrl(`${window.location.origin}/r/${data.share_token}`);
      setShareOpen(true);
    } catch (e) {
      toast.error("Could not create share link");
    } finally {
      setSharing(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied to clipboard");
    } catch (e) {
      toast.error("Copy failed — select and copy manually");
    }
  };

  return (
    <div className="space-y-6">
      {/* URL bar */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-500 min-w-0">
          <Globe className="w-4 h-4 flex-shrink-0" />
          <a href={report.url} target="_blank" rel="noreferrer" className="hover:underline truncate font-medium text-gray-700">
            {report.url}
          </a>
        </div>
        {report.id && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="outline"
              onClick={openShare}
              disabled={sharing}
              data-testid="share-report-button"
              className="border-gray-300"
            >
              <Share2 className="w-4 h-4 mr-2" />
              {sharing ? "…" : "Share"}
            </Button>
            {unlocked ? (
              <Button
                variant="outline"
                onClick={downloadPdf}
                disabled={downloading}
                data-testid="download-pdf-button"
                className="border-gray-300"
              >
                <FileDown className="w-4 h-4 mr-2" />
                {downloading ? "Generating…" : "Download PDF"}
              </Button>
            ) : (
              <Button
                onClick={doUnlock}
                disabled={unlocking}
                data-testid="unlock-report-button"
                className="bg-klein hover:bg-blue-800 text-white"
              >
                <Lock className="w-4 h-4 mr-2" />
                {unlocking ? "Unlocking…" : "Unlock report"}
              </Button>
            )}
          </div>
        )}
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Share this report</DialogTitle>
            <DialogDescription>
              Anyone with this link can view a read-only version of this audit — no login required.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input readOnly value={shareUrl} data-testid="share-link-input" className="text-sm" />
            <Button onClick={copyLink} data-testid="copy-share-link" className="bg-[#0A0A0A] hover:bg-gray-800 flex-shrink-0">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Top grid: gauge + summary + meta */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div
          className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 flex items-center justify-center animate-fade-up"
          data-testid="seo-score-gauge"
        >
          <ScoreGauge score={report.score} />
        </div>

        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-4 animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <StatCard label="Passed" value={report.summary?.good} color="#00C853" />
          <StatCard label="Warnings" value={report.summary?.warning} color="#FFD600" />
          <StatCard label="Errors" value={report.summary?.error} color="#FF3B30" />
          <StatCard label="Words" value={report.meta?.word_count} />
          <StatCard label="Load Time" value={`${report.meta?.load_time}s`} />
          <StatCard label="Page Size" value={`${report.meta?.page_size_kb}KB`} />
          <StatCard label="Images" value={report.meta?.images} />
          <StatCard label="Int. Links" value={report.meta?.internal_links} />
          <StatCard label="Ext. Links" value={report.meta?.external_links} />
        </div>
      </div>

      {/* Checks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-up" style={{ animationDelay: "0.15s" }}>
        <CheckList title="On-Page Checks" checks={report.onpage_checks || []} testid="onpage-checks" />
        <CheckList title="Technical Checks" checks={report.technical_checks || []} testid="technical-checks" />
      </div>

      {/* Keyword chart */}
      {kwData.length > 0 && (
        <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 animate-fade-up" data-testid="keyword-chart">
          <h3 className="uppercase-label mb-6">Keyword Density (Top {kwData.length})</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={kwData} margin={{ left: -20 }}>
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#9CA3AF" }} axisLine={false} tickLine={false} unit="%" />
              <Tooltip
                cursor={{ fill: "#F3F4F6" }}
                contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13 }}
                formatter={(v, n, p) => [`${v}%  (${p.payload.count}x)`, "Density"]}
              />
              <Bar dataKey="density" radius={[6, 6, 0, 0]}>
                {kwData.map((_, i) => (
                  <Cell key={i} fill="#002FA7" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* AI recommendations */}
      <div
        className="bg-gray-50 border-l-4 border-l-klein rounded-r-xl p-6 animate-fade-up"
        data-testid="ai-recommendations"
      >
        <div className="flex items-center gap-2 mb-5">
          <Sparkles className="w-5 h-5 text-klein" />
          <h3 className="font-heading text-xl font-bold tracking-tight">AI Recommendations</h3>
        </div>
        {(!report.recommendations || report.recommendations.length === 0) ? (
          <p className="text-gray-500 text-sm">No AI recommendations available for this report.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {report.recommendations.map((r, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-4" data-testid={`recommendation-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border capitalize ${PRIORITY[r.priority] || PRIORITY.medium}`}>
                    {r.priority}
                  </span>
                  <span className="text-xs text-gray-400 font-medium">{r.category}</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">{r.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{r.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Backlinks + Core Web Vitals (owner only, premium — gated the same as fixes) */}
      {report.id && unlocked && <CoreWebVitals reportId={report.id} initialData={report.vitals} />}
      {report.id && unlocked && <Backlinks reportId={report.id} initialData={report.backlinks} />}

      {/* Ready-to-apply fixes (owner only, premium) */}
      {report.id && unlocked && <SeoFixes reportId={report.id} />}
      {report.id && !unlocked && (
        <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-8 text-center animate-fade-up" data-testid="premium-locked">
          <div className="w-12 h-12 rounded-full bg-klein/10 flex items-center justify-center mx-auto">
            <Lock className="w-6 h-6 text-klein" />
          </div>
          <h3 className="font-heading text-xl font-bold tracking-tight mt-4">Unlock the full report</h3>
          <p className="text-gray-500 mt-1 max-w-md mx-auto">
            Get the downloadable PDF, ready-to-apply AI fixes (title, meta, schema, alt text) and one-click WordPress push — with a plan unlock or 1 credit.
          </p>
          <Button onClick={doUnlock} disabled={unlocking} data-testid="unlock-report-cta" className="mt-5 bg-klein hover:bg-blue-800 text-white px-6 py-6">
            <Lock className="w-4 h-4 mr-2" /> {unlocking ? "Unlocking…" : "Unlock report"}
          </Button>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-4 flex flex-col justify-center">
      <span className="uppercase-label">{label}</span>
      <span className="font-heading text-3xl font-black tracking-tighter mt-1" style={color ? { color } : {}}>
        {value ?? "—"}
      </span>
    </div>
  );
}
